import { Component, computed, inject, input, output } from '@angular/core';
import {
  DynamicTranslationService,
  getCurrencySign,
  NumberFormatConfig,
} from '@gs-2.0/core';

import { FundDisplay } from '../fund-switcher/fund-switcher.component';

/**
 * Balance information to display in the fund overview
 */
export interface BalanceInfo {
  /**
   * Total fund balance amount
   */
  totalBalance: number;
  /**
   * Available balance amount
   */
  availableBalance: number;
  /**
   * Currency code for the balance (e.g., 'USD', 'EUR')
   */
  currencyCode: string;
  /**
   * Optional country code for the currency flag (e.g., 'US', 'GB')
   * If not provided, will be derived from currencyCode
   */
  countryCode?: string;
}

/**
 * A fund overview component that displays fund balance information with a gradient background.
 * Based on Figma design.
 * Figma URL: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining---Final-Pages?node-id=1058-35999
 *
 * @example
 * ```html
 * <app-fund-overview
 *   userName="Gian"
 *   [funds]="availableFunds"
 *   [selectedFundId]="currentFundId"
 *   [balanceInfo]="{
 *     totalBalance: 1613.75,
 *     availableBalance: 1253.46,
 *     currencyCode: 'USD'
 *   }"
 *   [timestamp]="new Date('2025-10-02')"
 *   (fundChanged)="onFundChange($event)">
 * </app-fund-overview>
 * ```
 */
@Component({
  selector: 'app-fund-overview',
  templateUrl: './fund-overview.component.html',
  styleUrls: ['./fund-overview.component.scss'],
  standalone: false,
})
export class FundOverviewComponent {
  translationService = inject(DynamicTranslationService);
  /**
   * The name of the user to display in the welcome message
   */
  userName = input.required<string>();

  /**
   * Array of available funds for the fund switcher
   */
  funds = input<FundDisplay[]>([]);

  /**
   * The ID of the currently selected fund
   */
  selectedFundId = input<string>('');

  /**
   * Balance information to display
   */
  balanceInfo = input.required<BalanceInfo>();

  /**
   * Timestamp to display (e.g., "As of October 2, 2025")
   * If not provided, current date will be used
   */
  timestamp = input<Date>(new Date());

  /**
   * Placeholder text for fund switcher when no fund is selected
   */
  fundSwitcherPlaceholder = input<string>('Select a fund');

  /**
   * Whether data is currently loading from API
   */
  isLoading = input<boolean>(false);

  /**
   * Number format configuration from global config.
   */
  numberFormat = input<NumberFormatConfig>({
    decimalSymbol: '.',
    digitGroupingSymbol: ',',
    digitGrouping: '123,456,789',
  });

  /**
   * Date format from global config
   */
  dateFormat = input<string>('MM/dd/yyyy');

  /**
   * Emits when a different fund is selected
   */
  fundChanged = output<FundDisplay>();

  /**
   * Computed welcome message without comma when loading
   */
  protected welcomeMessage = computed(() => {
    const name = this.userName();
    return !name ? 'fund.welcomeBack' : 'fund.welcome_back_with_name';
  });

  /**
   * Formatted total balance with currency symbol.
   * Uses global config number format directly (same approach as activity page).
   */
  protected formattedTotalBalance = computed(() => {
    const balanceInfo = this.balanceInfo();
    const loading = this.isLoading();

    if (loading && balanceInfo.totalBalance === 0) {
      return this.formatNumberValue(balanceInfo.totalBalance);
    }

    const currencySign = getCurrencySign(balanceInfo.currencyCode);
    return `${currencySign}${this.formatNumberValue(balanceInfo.totalBalance)}`;
  });

  /**
   * Formatted available balance with currency symbol.
   * Uses global config number format directly (same approach as activity page).
   */
  protected formattedAvailableBalance = computed(() => {
    const balanceInfo = this.balanceInfo();
    const loading = this.isLoading();

    if (loading && balanceInfo.availableBalance === 0) {
      return this.formatNumberValue(balanceInfo.availableBalance);
    }

    const currencySign = getCurrencySign(balanceInfo.currencyCode);
    return `${currencySign}${this.formatNumberValue(balanceInfo.availableBalance)}`;
  });

  /**
   * Formatted timestamp string
   */
  protected formattedTimestamp = computed(() => {
    const date = this.timestamp();
    const format = this.dateFormat();
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return format
      .replace(/yyyy/g, String(year))
      .replace(/yy/g, String(year).slice(-2))
      .replace(/MMM/g, '\x01')
      .replace(/MM/g, String(month).padStart(2, '0'))
      .replace(/M(?!M)/g, String(month))
      .replace(/dd/g, String(day).padStart(2, '0'))
      .replace(/d(?!d)/g, String(day))
      .replace(/\x01/g, ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][month - 1]);
  });

  /**
   * Handles fund selection from fund switcher
   */
  onFundChanged(fund: FundDisplay): void {
    this.fundChanged.emit(fund);
  }

  /**
   * Formats a number using global config number format values directly.
   * Same pattern as the activity page's formatNumberValue.
   */
  private formatNumberValue(value: number): string {
    const fmt = this.numberFormat();
    const parts = Math.abs(value).toFixed(2).split('.');
    const intPart = parts[0];
    const decPart = parts[1];
    const grouped = this.applyDigitGrouping(intPart, fmt.digitGroupingSymbol, fmt.digitGrouping);
    const result = grouped + fmt.decimalSymbol + decPart;
    return value < 0 ? `-${result}` : result;
  }

  private applyDigitGrouping(intPart: string, groupSymbol: string, groupPattern: string): string {
    const patternParts = groupPattern.replace(/[^0-9,]/g, '').split(',');
    if (patternParts.length <= 1) return intPart;

    const groupSizes = patternParts.map((p) => p.length).reverse();
    const digits = intPart.split('').reverse();
    const groups: string[] = [];
    let pos = 0;
    let sizeIdx = 0;

    while (pos < digits.length) {
      const size = groupSizes[Math.min(sizeIdx, groupSizes.length - 1)];
      groups.push(digits.slice(pos, pos + size).reverse().join(''));
      pos += size;
      sizeIdx++;
    }

    return groups.reverse().join(groupSymbol);
  }
}
